<template>
  <div class="document-container">
    <es-loader v-if="loading"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <template>
        <div class="modal is-active"
          role="dialog"
          :aria-label="this.userAction.actionConfirmHeader"
          aria-hidden="false"
          v-if="showModal"
        >
          <div class="modal-background"></div>
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">
                {{this.userAction.actionConfirmHeader}}
              </p>
              <button
                class="icon is-right"
                @click.prevent="toggleModal()"
                ref="closeIcon"
              >
                <font-awesome-icon :icon="['fal', 'times']" title="Close" />
              </button>
            </header>
            <section class="modal-card-body">
              <div class="modal-body">
                {{this.userAction.actionConfirmMessage}}
              <br/>
              <br/>
              </div>
              <div class="update-box">
                <button @click.prevent="toggleModal()"
                    :class="{ disabled: loading }"
                    class="button button-link has-underline"
                    :disabled="loading"
                  >
                  Cancel
                </button>
                <button
                  class="button is-primary"
                  @click="processUserAction()"
                  :disabled="loading"
                >
                  {{this.userAction.actionConfirmButtonText}}
                </button>
              </div>
            </section>
          </div>
        </div>
      </template>
      <div class="document">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                Compare Records
              </div>
              <div class="header-subtitle">
                The {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                for the submission record are displayed below.
              </div>
            </template>
            <template #actions>
              <div class="summary">
                <div class="separator"></div>
                <div class="p-narrow list-content">
                  <div class="list-header">
                    <div class="list-title">
                      {{ batchId || '--'}}
                    </div>
                    <div class="list-subtitle">
                      Batch ID
                    </div>
                  </div>
                </div>
                <div class="separator"></div>
                <div class="p-narrow list-content">
                  <div class="list-header">
                    <div class="list-title">
                      {{ nearMatchId }}
                    </div>
                    <div class="list-subtitle">
                      Transaction ID
                    </div>
                  </div>
                </div>
                <div class="separator"></div>
                <div class="p-narrow list-content">
                  <div class="list-header is-flex flex-align--center">
                    <div v-if="summaryStatus.statusName==='Pending'">
                      <font-awesome-icon
                        :icon="summaryStatus.statusIcon"
                        style="font-size: 25px; color: FFC107"
                      />
                    </div>
                    <div v-else-if="summaryStatus.statusName==='Resolved'">
                      <font-awesome-icon
                        :icon="summaryStatus.statusIcon"
                        style="font-size: 25px; color: 008000"
                      />
                    </div>
                    <div v-else-if="summaryStatus.statusName==='Canceled'">
                      <font-awesome-icon
                        :icon="summaryStatus.statusIcon"
                        style="font-size: 25px; color: 808080"
                      />
                    </div>
                    <strong class="list-title" style="padding-left: 0.5rem">
                      {{ summaryStatus.statusName }}
                    </strong>

                  </div>
                </div>
              </div>
            </template>
          </es-header>
        </div>
        <div class="padding--1rem">
          <div style="max-width: 1200px; margin: auto;">
            <!-- status panel -->
            <pid-compare-status-panel
              style="max-width: 1200px; margin: auto; margin-bottom: 1rem;"
              :batch-id="batchId"
              :decision-status="decisionStatus"
              :match-status-icon="summaryStatus.statusIcon"
              :near-matches="nearMatches"
              :near-match-id="nearMatchId"
              :is-resolved-match="isResolvedMatch"
              :is-resolved-no-match="isResolvedNoMatch"
              :pending-count="pendingCount"
              :newly-created-id="newlyCreatedId"
            />
            <!-- action buttons -->
            <div class="field is-grouped is-flex flex-justify--end">
              <template v-if="decisionStatus=='Pending'">
                <button
                  v-if="hasDistrictSchoolPermission(
                    matchPermission('PRSID:MATCH:RESOLVENM:CANCEL')
                  )"
                  class="button button-link has-underline"
                  style="margin-right: 1rem"
                  @click.prevent="toggleModal('Cancel Submission Record','cancelRef1')"
                  ref="cancelRef1"
                >
                  Cancel Submission Record
                </button>
                <button
                  v-if="userCanPerformNoMatch()"
                  class="button is-link is-light"
                  @click.prevent="toggleModal('No Match','noMatchRef1')"
                  ref="noMatchRef1"
                >
                  {{ properties['BUTTON_LABEL-CREATE_NEW'] }}
                </button>
              </template>
            </div>
          </div>
          <div class="outer">
            <div class="inner">
              <table
                class="table is-fullwidth"
                v-for="(nearMatch, index) in nearMatches"
                :key="index"
              >
                <tbody>
                  <tr>
                    <th class="compare-panel vertical-align--initial">
                      <div class="text-align--center">
                        SUBMISSION RECORD
                      </div>
                      <div class="text-align--center">
                        <div class="source-label has-text-weight-bold">
                          Source: {{ nearMatch.pidBatch.submissionTypeCode }}
                        </div>
                      </div>
                    </th>
                    <th
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel"
                    >
                      <div class="is-flex flex-direction--column flex-align--center" >
                        <div class="is-size-8 is-uppercase">
                          {{ properties['SYSTEM_LABEL-NEARMATCH'] }} RECORD
                        </div>
                        <div class="text-align--center margin-bottom--half">
                          <div class="source-label has-text-weight-bold">
                            Source: Master Record
                          </div>
                        </div>
                        <div>
                          <vc-donut
                            class="margin-bottom--half"
                            :size="60"
                            :sections="[
                            { value: potentialMatch.percent - 1, color: '#215E97' },
                            { value: 1, color: '#FFFFFF' },
                            { value: 99 - potentialMatch.percent, color: '#C8C8C8'},
                            { value: 1, color: '#FFFFFF' },]"
                          >
                            <h1 class="percent-label">
                              {{ potentialMatch.percent }}%
                            </h1>
                          </vc-donut>
                          <div class="has-text-grey margin-bottom--half">
                            Match Score
                          </div>
                        </div>
                        <template v-if="decisionStatus=='Pending'">
                          <template v-if="userCanPerformMatch()">
                            <button
                              class="button is-primary"
                              @click.prevent="toggleModalMatch('Match',potentialMatch.id,'matchRef1',index)"
                              ref="matchRef1"
                            >
                              {{ properties['BUTTON_LABEL-ASSIGN_EXISTING']}}: ID
                              {{ formatStateID(potentialMatch.id) }}
                            </button>
                            <update-master-yes-no
                              v-if="userHasUpdateMasterPermission(potentialMatch.id)"
                              :potentialMatchId=potentialMatch.id
                              @update-master-changed="setUpdateMaster"
                            />
                          </template>
                        </template>
                        <div v-else-if="decisionStatus=='Canceled'">
                          Canceled
                        </div>
                        <div
                          v-else-if="decisionStatus=='Match' && potentialMatch.id == finalMatchId"
                        >
                          Matched: {{ formatStateID(potentialMatch.id) }}
                        </div>
                        <div v-else>
                          No Match
                        </div>
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Matching Fields
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel--heading text-align--center"
                    >
                      <div v-if="fieldsNotMatchSubmission(matchingFieldNames,
                        potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('FIRST_NAME', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.firstName }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.firstName,
                          getMasterDataElement('firstName', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('firstName', potentialMatch.matchedObject.id) }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="getFieldEnabledForPersonType('MIDDLE_NAME', 'ASSIGN',
                  nearMatch.pidSubmission.personType)">
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('MIDDLE_NAME', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.middleName | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.middleName,
                          getMasterDataElement('middleName', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('middleName', potentialMatch.matchedObject.id)
                          | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('LAST_NAME', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.lastName }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.lastName,
                          getMasterDataElement('lastName', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('lastName', potentialMatch.matchedObject.id) }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="getFieldEnabledForPersonType('SUFFIX', 'ASSIGN',
                  nearMatch.pidSubmission.personType)">
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('SUFFIX', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.suffix | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.suffix,
                          getMasterDataElement('suffix', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('suffix', potentialMatch.matchedObject.id)
                          | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="getFieldEnabledForPersonType('PREVIOUS_LAST_NAME', 'ASSIGN',
                  nearMatch.pidSubmission.personType)">
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('PREVIOUS_LAST_NAME', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.previousLastName | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.previousLastName,
                          getMasterDataElement('previousLastName', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('previousLastName', potentialMatch.matchedObject.id)
                          | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('GENDER_CODE', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatch.pidSubmission.genderCode | getDescripton(activeGenders) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.genderCode,
                          getMasterDataElement('genderCode', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ getMasterDataElement('genderCode', potentialMatch.matchedObject.id)
                          | getDescripton(activeGenders) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelByPersonType('DATE_OF_BIRTH', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div class="compare--content">
                          {{ displayDOBFormat(nearMatch.pidSubmission.dateOfBirth) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.dateOfBirth,
                          getMasterDataElement('dateOfBirth', potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        {{ displayDOBFormat(getMasterDataElement('dateOfBirth',
                          potentialMatch.matchedObject.id)) }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="getFieldEnabledForPersonType('SOCIAL_SECURITY_NUMBER', 'ASSIGN',
                    nearMatch.pidSubmission.personType)">
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{  getFieldLabelByPersonType('SOCIAL_SECURITY_NUMBER', 'ASSIGN',
                            nearMatch.pidSubmission.personType) }}
                        </div>
                        <div
                          v-if="hasDistrictSchoolPermission(matchPermission(viewSsnPermission))"
                          class="compare--content"
                        >
                          {{
                            formatSsn(nearMatch.pidSubmission.socialSecurityNumber) | OrPlaceHolder
                          }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--cell vertical-align--bottom"
                      :class="{
                        'panel--highlighted': fieldsNotMatch(
                          nearMatch.pidSubmission.socialSecurityNumber,
                          getMasterDataElement('socialSecurityNumber',
                              potentialMatch.matchedObject.id)
                        ),
                      }"
                    >
                      <div class="compare--content">
                        <template>
                          <span v-if="hasDistrictSchoolPermission(matchPermission(
                            viewSsnPermission)) && getMasterDataElement('socialSecurityNumber',
                              potentialMatch.matchedObject.id)">
                            {{ formatSsn(getMasterDataElement('socialSecurityNumber',
                              potentialMatch.matchedObject.id))
                              | OrPlaceHolder }}
                          </span>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Person Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel--heading text-align--center"
                    >
                      <div v-if="fieldsNotMatchSubmission(personFieldNames,
                        potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.PERSON)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.PERSON]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.PERSON]">
                    <tr v-if="getFieldEnabledForPersonType('STATE_STUDENT_ID', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('STATE_STUDENT_ID', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.stateStudentId }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.stateStudentId,
                            getMasterDataElement('stateStudentId',potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('stateStudentId',
                              potentialMatch.matchedObject.id) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ETHNICITY_INDICATOR', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ETHNICITY_INDICATOR', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.ethnicityIndicator
                              | getDescripton(activeEthnicities) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.ethnicityIndicator,
                            getMasterDataElement('ethnicityIndicator',potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('ethnicityIndicator',
                              potentialMatch.matchedObject.id)
                              | getDescripton(activeEthnicities) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RACE_ETHNIC_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RACE_ETHNIC_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.raceEthnicCode | getDescripton(activeRaces) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.raceEthnicCode,
                            getMasterDataElement('raceEthnicCode', potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('raceEthnicCode',
                              potentialMatch.matchedObject.id)
                            | getDescripton(activeRaces) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RACE2_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{getFieldLabelByPersonType('RACE2_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.race2Code | getDescripton(activeRaces)}}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.race2Code,
                            getMasterDataElement('race2Code',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('race2Code',
                              potentialMatch.matchedObject.id) | getDescripton(activeRaces) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RACE3_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RACE3_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.race3Code | getDescripton(activeRaces) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.race3Code,
                            getMasterDataElement('race3Code',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('race3Code',
                              potentialMatch.matchedObject.id) | getDescripton(activeRaces) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RACE4_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RACE4_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.race4Code | getDescripton(activeRaces) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.race4Code,
                            getMasterDataElement('race4Code',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('race4Code',
                              potentialMatch.matchedObject.id)  | getDescripton(activeRaces) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RACE5_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RACE5_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.race5Code | getDescripton(activeRaces) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.race5Code,
                            getMasterDataElement('race5Code',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('race5Code',
                              potentialMatch.matchedObject.id) | getDescripton(activeRaces) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ETHNIC_SUB_GROUP', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ETHNIC_SUB_GROUP', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.ethnicSubGroup
                            | getDescripton(activeEthnicSubGroups)  }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.ethnicSubGroup,
                            getMasterDataElement('ethnicSubGroup',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('ethnicSubGroup',
                              potentialMatch.matchedObject.id) |
                              getDescripton(activeEthnicSubGroups) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('FULL_NAME', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('FULL_NAME', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.fullName | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.fullName,
                            getMasterDataElement('fullName',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('fullName',
                              potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('AKA_NAME', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('AKA_NAME', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.akaName | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.akaName,
                            getMasterDataElement('akaName',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('akaName',
                              potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Contact Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel--heading text-align--center"
                      style="text-align:center"
                    >
                        <div v-if="fieldsNotMatchSubmission(contactFieldNames,
                          potentialMatch.matchedObject.id)">
                          <font-awesome-icon
                            title="Section Contains Differences"
                            :icon="exclamationIcon"
                            style="font-size: 25px; color: FF0000"
                          />
                        </div>
                        <button
                          @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.CONTACT)"
                          class="compare-panel-toggle button button-link"
                          v-if="index === potentialMatchesForDisplay.length - 1">
                          <font-awesome-icon :icon="['far', 'caret-up']"
                            class="fa-2xl"
                            title="Hide Details"
                            v-if="expandedSections[EXPANDABLE_SECTIONS.CONTACT]"
                          />
                          <font-awesome-icon :icon="['far', 'caret-down']"
                            class="fa-2xl"
                            title="View Details"
                            v-else
                          />
                        </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.CONTACT]">
                    <tr v-if="getFieldEnabledForPersonType('STREET_1', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('STREET_1', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.street1 | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.street1,
                            getMasterDataElement('street1',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('street1',
                              potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('STREET_2', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('STREET_2', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.street2 | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.street2,
                            getMasterDataElement('street2',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('street2',
                              potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('CITY', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('CITY', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.city | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.city,
                            getMasterDataElement('city',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('city',
                              potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('STATE_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('STATE_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.stateCode | getDescripton(activeStates) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.stateCode,
                            getMasterDataElement('stateCode',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('stateCode',
                              potentialMatch.matchedObject.id) | getDescripton(activeStates)  }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ZIP', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ZIP', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.zip | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.zip,
                            getMasterDataElement('zip',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('zip',
                              potentialMatch.matchedObject.id)  | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('GEOCODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('GEOCODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.geocode | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.geocode,
                            getMasterDataElement('geocode', potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('geocode',
                              potentialMatch.matchedObject.id)  | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Customer Defined Fields -->
                  <tr v-if="enabledCdfFieldNames.length > 0">
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Customer Defined Fields
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                    >
                      <div v-if="cdfFieldsNotMatchSubmission(potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.CUSTOMER)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.CUSTOMER]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.CUSTOMER]">
                    <tr v-for="(cdfFieldName, index) in enabledCdfFieldNames"
                      :key="index">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType(cdfFieldName, 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.studentCdf[cdfFieldName] | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.studentCdf[cdfFieldName],
                            getMasterCdfDataElement(cdfFieldName, potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterCdfDataElement(cdfFieldName, potentialMatch.matchedObject.id)
                          | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Location Information -->
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Location Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                    >
                      <div v-if="locationFieldsNotMatchSubmission(potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.LOCATION)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.LOCATION]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.LOCATION]">
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Person Type
                          </div>
                          <div class="compare--content">
                            {{ mapPersonType(nearMatch.pidSubmission.personType) | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.personType,
                            getMasterDataElement('personType',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ mapPersonType(getMasterDataElement('personType',
                              potentialMatch.matchedObject.id))  | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SCHOOL_YEAR', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SCHOOL_YEAR', 'ASSIGN',
                              nearMatch.pidSubmission.personType, 'PER') }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.schoolYear | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.schoolYear,
                            convertToString(getMasterDataElement('schoolYear',
                              potentialMatch.matchedObject.id))
                          ),
                        }"
                      >
                        <div class="compare--content">
                            <span v-if="getMasterDataElement('schoolYear',
                              potentialMatch.matchedObject.id) ">
                              {{ convertToString(getMasterDataElement('schoolYear',
                                potentialMatch.matchedObject.id))
                                | OrPlaceHolder }}
                            </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('DISTRICT_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('DISTRICT_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.districtCode | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.districtCode,
                            getMasterDataElement('districtCode',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('districtCode',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SCHOOL_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SCHOOL_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType)}}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.schoolCode | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.schoolCode,
                            getMasterDataElement('schoolCode',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('schoolCode',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RES_DISTRICT_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RES_DISTRICT_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.resDistrictCode | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.resDistrictCode,
                            getMasterDataElement('resDistrictCode',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('resDistrictCode',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('LOCAL_STUDENT_ID', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('LOCAL_STUDENT_ID', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.localStudentId | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.localStudentId,
                            getMasterDataElement('localId',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('localId',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SOURCE_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SOURCE_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.sourceCode | getDescripton(activeSources) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.sourceCode,
                            getMasterDataElement('sourceCode',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('sourceCode',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('sourceCode',
                                potentialMatch.matchedObject.id)
                              | getDescripton(activeSources) }}
                          </span>
                        </div>
                      </td>
                    </tr>

                    <!-- STAFF FIELDS GO HERE -->
                    <tr v-if="getFieldEnabledForPersonType('GRADE_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('GRADE_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.gradeCode | getDescripton(activeGrades) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.gradeCode,
                            getMasterDataElement('gradeCode', potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('gradeCode',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('gradeCode',
                                potentialMatch.matchedObject.id) | getDescripton(activeGrades) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('HIRE_DATE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('HIRE_DATE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ formatMMDDYYYY(nearMatch.pidSubmission.hireDate) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.hireDate,
                            getMasterDataElement('hireDate',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('hireDate',
                                potentialMatch.matchedObject.id)">
                            {{ formatMMDDYYYY(getMasterDataElement('hireDate',
                                potentialMatch.matchedObject.id)) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SALARY', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SALARY', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.salary }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.salary,
                            getMasterDataElement('salary',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('salary',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('salary',
                                potentialMatch.matchedObject.id) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('JOB_CLASS_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('JOB_CLASS_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.jobClassCode | getDescripton(activeTitles) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.jobClassCode,
                            getMasterDataElement('jobClassCode',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('jobClassCode',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('jobClassCode',
                                potentialMatch.matchedObject.id) | getDescripton(activeTitles) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('JOB_ASSIGNMENT_1', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('JOB_ASSIGNMENT_1', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.jobAssignment1 | getDescripton(activeJobAssignments) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.jobAssignment1,
                            getMasterDataElement('jobAssignment1',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('jobAssignment1',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('jobAssignment1',
                                potentialMatch.matchedObject.id) | getDescripton(activeJobAssignments) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('JOB_ASSIGNMENT_2', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('JOB_ASSIGNMENT_2', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.jobAssignment2 | getDescripton(activeJobAssignments)  }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.jobAssignment2,
                            getMasterDataElement('jobAssignment2',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('jobAssignment2',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('jobAssignment2',
                                potentialMatch.matchedObject.id) | getDescripton(activeJobAssignments)  }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('JOB_ASSIGNMENT_3', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('JOB_ASSIGNMENT_3', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.jobAssignment3 | getDescripton(activeJobAssignments) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.jobAssignment3,
                            getMasterDataElement('jobAssignment3',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('jobAssignment3',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('jobAssignment3',
                                potentialMatch.matchedObject.id) | getDescripton(activeJobAssignments) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ITINERANT_FLAG', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ITINERANT_FLAG', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.itinerantFlag | getDescripton(activeItinerantFlags) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.itinerantFlag,
                            getMasterDataElement('itinerantFlag',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('itinerantFlag',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('itinerantFlag',
                                potentialMatch.matchedObject.id) | getDescripton(activeItinerantFlags) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ALT_STUDENT_ID', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ALT_STUDENT_ID', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.altStudentId }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.altStudentId,
                            getMasterDataElement('altStudentId',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('altStudentId',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('altStudentId',
                                potentialMatch.matchedObject.id) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('ALT_SOURCE_SIS_CODE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('ALT_SOURCE_SIS_CODE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.altSourceSisCode | getDescripton(activeSources) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.altSourceSisCode,
                            getMasterDataElement('altSourceSisCode',
                              potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          <span v-if="getMasterDataElement('altSourceSisCode',
                                potentialMatch.matchedObject.id)">
                            {{ getMasterDataElement('altSourceSisCode',
                                potentialMatch.matchedObject.id) | getDescripton(activeSources) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Active Location Count
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.locationCount | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.locationCount,
                            getMasterDataElement('activeLocationCount',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('activeLocationCount',
                                potentialMatch.matchedObject.id) | OrZeroPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SOURCE_REFRESH_DATE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SOURCE_REFRESH_DATE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.sourceRefreshDate | formatDatePreserveZone }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            toDateInputValue(nearMatch.pidSubmission.sourceRefreshDate),
                            toDateInputValue(getMasterDataElement('sourceRefreshDate',
                                potentialMatch.matchedObject.id))
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('sourceRefreshDate',
                                potentialMatch.matchedObject.id) | formatDatePreserveZone }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('LOCATION_COMMENT', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('LOCATION_COMMENT', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.comments | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.comments,
                            getMasterDataElement('locationComment',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('locationComment',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="credentialInfoSectionEnabled">
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Credential Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                    >
                      <div v-if="fieldsNotMatchSubmission(credentialFieldNames,
                        potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.CREDENTIAL)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.CREDENTIAL]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.CREDENTIAL] && credentialInfoSectionEnabled">
                    <tr v-if="getFieldEnabledForPersonType('HIGHEST_DEGREE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('HIGHEST_DEGREE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.highestDegree | getDescripton(activeHighestDegrees) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.highestDegree,
                            getMasterDataElement('highestDegree',
                                potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterDataElement('highestDegree',
                                potentialMatch.matchedObject.id) | getDescripton(activeHighestDegrees) }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('SUBJECT_MATTER_AREA', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('SUBJECT_MATTER_AREA', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.subjectMatterArea | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.subjectMatterArea,
                            getMasterDataElement('subjectMatterArea',
                                potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterDataElement('subjectMatterArea',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('DEGREE_PROVIDER', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('DEGREE_PROVIDER', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.degreeProvider | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.degreeProvider,
                            getMasterDataElement('degreeProvider',
                                potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterDataElement('degreeProvider',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('CERTIFICATE_NUMBER', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('CERTIFICATE_NUMBER', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.certificateNumber | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.certificateNumber,
                            getMasterDataElement('certificateNumber',
                                potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterDataElement('certificateNumber',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('CERTIFICATE_TYPE', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('CERTIFICATE_TYPE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.certificateType | getDescripton(activeCertificateTypes) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.certificateType,
                            getMasterDataElement('certificateType',
                                potentialMatch.matchedObject.id)
                          ),
                        }">
                        <div class="compare--content">
                          {{ getMasterDataElement('certificateType',
                                potentialMatch.matchedObject.id) | getDescripton(activeCertificateTypes) }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Birth Location -->
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Other Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                    >
                      <div v-if="fieldsNotMatchSubmission(otherInfoFieldNames,
                        potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.OTHER)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.OTHER]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.OTHER]">
                    <tr v-if="getFieldEnabledForPersonType('BIRTH_LOCATION', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('BIRTH_LOCATION', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.birthLocation | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.birthLocation,
                            getMasterDataElement('birthLocation',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('birthLocation',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('RESIDENCY_STATUS', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RESIDENCY_STATUS', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.residencyStatus | getDescripton(activeResidencyStatuses) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.residencyStatus,
                            getMasterDataElement('residencyStatus',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('residencyStatus',
                                potentialMatch.matchedObject.id) | getDescripton(activeResidencyStatuses) }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('RESIDENCY_EXPIRATION_DATE', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.residencyExpirationDate | formatDateMMDDYYYY }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.residencyExpirationDate,
                            getMasterDataElement('residencyExpirationDate',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('residencyExpirationDate',
                                potentialMatch.matchedObject.id) | formatDateMMDDYYYY }}
                        </div>
                      </td>
                    </tr>
                    <tr v-if="getFieldEnabledForPersonType('MOTHERS_MAIDEN_NAME', 'ASSIGN',
                      nearMatch.pidSubmission.personType)">
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            {{ getFieldLabelByPersonType('MOTHERS_MAIDEN_NAME', 'ASSIGN',
                              nearMatch.pidSubmission.personType) }}
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.mothersMaidenName | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.mothersMaidenName,
                            getMasterDataElement('mothersMaidenName',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('mothersMaidenName',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Transaction Information -->
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Transaction Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                    >
                      <div v-if="fieldsNotMatchSubmission(transactionFieldNames,
                        potentialMatch.matchedObject.id)">
                        <font-awesome-icon
                          title="Section Contains Differences"
                          :icon="exclamationIcon"
                          style="font-size: 25px; color: FF0000"
                        />
                      </div>
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.TRANSACTION)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.TRANSACTION]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.TRANSACTION]">
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Transaction Type
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidBatch.submissionTypeCode }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidBatch.submissionTypeCode,
                            getMasterDataElement('submissionTypeCode',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('submissionTypeCode',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Last Transaction ID
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmissionId | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmissionId,
                            getMasterDataElement('lastTranSerialNumber',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('lastTranSerialNumber',
                                potentialMatch.matchedObject.id) | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            BatchId
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidSubmission.pidBatchId | OrPlaceHolder }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidSubmission.pidBatchId,
                            getMasterDataElement('lastTranBatchId',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('lastTranBatchId',
                                potentialMatch.matchedObject.id)  | OrPlaceHolder }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Last Update Date
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidBatch.lastUpdateDate | formatDateMMDDYYYY}}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            formatMMDDYYYY(nearMatch.pidBatch.lastUpdateDate),
                            formatMMDDYYYY(getMasterDataElement('lastUpdateDate',
                                potentialMatch.matchedObject.id))
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('lastUpdateDate',
                                potentialMatch.matchedObject.id) | formatDateMMDDYYYY }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                        <td class="compare-panel compare-panel--cell">
                          <div class="">
                            <div class="submission-header has-text-left">
                              Last Updated User
                            </div>
                            <div class="compare--content">
                              {{
                                `${nearMatch.lastUpdateByFirstName}
                                ${nearMatch.lastUpdateByLastName}`
                              }}
                            </div>
                          </div>
                        </td>
                        <td
                          v-for="(potentialMatch, index) in
                            potentialMatchesForDisplay"
                          :key="index"
                          class=
                            "compare-panel compare-panel--cell vertical-align--bottom"
                          :class="{
                            'panel--highlighted': fieldsNotMatch(
                              nearMatch.pidBatch.lastUpdateBy,
                              getMasterDataElement('lastUpdateBy',
                                potentialMatch.matchedObject.id)
                            ),
                          }"
                        >
                          <div class="compare--content">
                            {{
                              getMasterDataElement(
                                'updateByFirstName',
                                potentialMatch.matchedObject.id
                              ) + ' ' +
                              getMasterDataElement(
                                'updateByLastName',
                                potentialMatch.matchedObject.id
                              )
                            }}
                          </div>
                        </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            ID Creation Date
                          </div>
                          <div class="compare--content">
                            {{ nearMatch.pidBatch.createdDate |  formatDateMMDDYYYY }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            formatMMDDYYYY(nearMatch.pidBatch.createdDate),
                            formatMMDDYYYY(getMasterDataElement('createdDate',
                                potentialMatch.matchedObject.id))
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{ getMasterDataElement('createdDate',
                                potentialMatch.matchedObject.id)  | formatDateMMDDYYYY }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            ID Created By
                          </div>
                          <div class="compare--content">
                            {{
                              `${nearMatch.createdByFirstName}
                              ${nearMatch.createdByLastName}`
                            }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        :class="{
                          'panel--highlighted': fieldsNotMatch(
                            nearMatch.pidBatch.createdBy,
                            getMasterDataElement('createdBy',
                                potentialMatch.matchedObject.id)
                          ),
                        }"
                      >
                        <div class="compare--content">
                          {{
                            getMasterDataElement(
                              'createdByFirstName',
                              potentialMatch.matchedObject.id
                            ) + ' ' +
                            getMasterDataElement(
                              'createdByLastName',
                              potentialMatch.matchedObject.id
                            )
                          }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Matching Information -->
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Matching Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center">
                      <button
                        @click.prevent="toggleDetails(EXPANDABLE_SECTIONS.MATCHING)"
                        class="compare-panel-toggle button button-link"
                        v-if="index === potentialMatchesForDisplay.length - 1">
                        <font-awesome-icon :icon="['far', 'caret-up']"
                          class="fa-2xl"
                          title="Hide Details"
                          v-if="expandedSections[EXPANDABLE_SECTIONS.MATCHING]"
                        />
                        <font-awesome-icon :icon="['far', 'caret-down']"
                          class="fa-2xl"
                          title="View Details"
                          v-else
                        />
                      </button>
                    </td>
                  </tr>
                  <template v-if="expandedSections[EXPANDABLE_SECTIONS.MATCHING]">
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Match Score
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                        >
                        <div class="compare--content">
                          {{getMatchPercent(potentialMatch.matchedObject.id)}}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Match Note
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                      >
                        <div class="compare--content">
                          {{ getMatchNote(potentialMatch.matchedObject.id) }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="compare-panel compare-panel--cell">
                        <div class="">
                          <div class="submission-header has-text-left">
                            Record Status
                          </div>
                          <div class="compare--content">
                            {{ getRecordStatusDescription(nearMatch.transStatus) }}
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="(potentialMatch, index) in
                          potentialMatchesForDisplay"
                        :key="index"
                        class="compare-panel compare-panel--cell vertical-align--bottom"
                      >
                        <div class="compare--content">
                          <!-- TO DO show status for the last tran for potential match -->
                          {{ getRecordStatusDescription(getMasterDataElement('lastTransStatus',
                            potentialMatch.matchedObject.id)) }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <!-- Actions per match -->
                  <tr>
                    <td class="compare--content">
                      &nbsp;
                    </td>
                     <td
                      v-for="(potentialMatch, index) in
                        potentialMatchesForDisplay"
                      :key="index"
                      class="compare-panel text-align--center"
                    >
                      <template v-if="decisionStatus=='Pending'">
                        <template
                          v-if="userCanPerformMatch()"
                        >
                          <button
                            class="button is-primary"
                            @click.prevent="toggleModalMatch('Match',potentialMatch.id,'matchRef2',index)"
                            ref="matchRef2"
                          >
                            {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}: ID
                            {{ formatStateID(potentialMatch.id) }}
                          </button>
                          <update-master-yes-no
                            v-if="userHasUpdateMasterPermission(potentialMatch.id)"
                            :potentialMatchId=potentialMatch.id
                            @update-master-changed="setUpdateMaster"/>
                        </template>
                      </template>
                      <div
                        v-else-if="decisionStatus=='Match' && potentialMatch.id == finalMatchId"
                        class="field is-grouped is-flex text-align--center">
                        Matched: {{ formatStateID(potentialMatch.id) }}
                      </div>
                      <div v-else-if="decisionStatus=='Canceled'">
                        Canceled
                      </div>
                      <div v-else
                        class="field is-grouped is-flex text-align--center"
                      >
                        No Match
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Actions at transaction level -->
          <div
            class="field is-grouped is-flex flex-justify--end"
          >
          <template v-if="decisionStatus=='Pending'">
            <button
                v-if="hasDistrictSchoolPermission(matchPermission('PRSID:MATCH:RESOLVENM:CANCEL'))"
                class="button button-link has-underline"
                style="margin-right: 1rem"
                @click.prevent="toggleModal('Cancel Submission Record', 'cancelRef2')"
                ref="cancelRef2"
              >
                Cancel Submission Record
              </button>
              <button
                v-if="userCanPerformNoMatch()"
                class="button is-link is-light"
                @click.prevent="toggleModal('No Match', 'noMatchRef2')"
                ref="noMatchRef2"
              >
                {{ properties['BUTTON_LABEL-CREATE_NEW'] }}
              </button>
          </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import moment from 'moment';
import Vue from 'vue';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import PidCompareStatusPanel from '@/components/PidCompareStatusPanel.vue';
import UpdateMasterYesNo from '@/components/UpdateMasterYesNo.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';
import StateIdUtils from '@/mixins/StateIdUtils';
import DateUtils from '@/mixins/DateUtils';
import AccessibilityUtils from '@/mixins/AccessibilityUtils';
import { areValuesDifferentIgnoringCase } from '@/mixins/Utils';
import {
  faCaretDown,
  faCaretUp,
} from '@fortawesome/pro-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

Vue.use(Donut);

library.add(
  faCaretDown,
  faCaretUp,
);

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    FieldLabels,
    DateUtils,
    StateIdUtils,
    AccessibilityUtils,
  ],
  components: {
    PidCompareStatusPanel,
    UpdateMasterYesNo,
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    nearMatchId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      EXPANDABLE_SECTIONS: {
        PERSON: 'person',
        CONTACT: 'contact',
        CUSTOMER: 'customer',
        LOCATION: 'location',
        CREDENTIAL: 'credential',
        OTHER: 'other',
        TRANSACTION: 'transaction',
        MATCHING: 'matching',
      },
      expandedSections: {},
      nearMatches: {},
      matchIds: {},
      loading: false,
      loadingError: false,
      messages: [],
      potentialMatchesForDisplay: [],
      doNotUpdateMasterIndicators: new Set(),
      decisionStatus: '',
      isResolvedMatch: false,
      isResolvedNoMatch: false,
      finalMatchId: -1,
      showModal: false,
      masterData: {},
      pendingCount: null,
      objectEntry: null,
      newlyCreatedId: -1,
      userAction: {
        actionConfirmButtonText: '',
        actionConfirmMessage: '',
        actionConfirmHeader: '',
        actionMethod: '',
        matchStatusIcon: '',
      },
      summaryStatus: {
        statusName: '',
        statusIcon: '',
      },
      refEle: '',
    };
  },
  async created() {
    await this.onLoad();
    Object.keys(this.EXPANDABLE_SECTIONS).forEach((key) => {
      this.$set(this.expandedSections, this.EXPANDABLE_SECTIONS[key], true);
    });
  },
  mounted() {
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'personId' } },
      { text: `Compare ${this.properties['SYSTEM_LABEL-NEARMATCH']}`, to: { } },
    ]);
  },
  computed: {
    ...mapGetters({
      hasDistrictSchoolPermission: 'hasDistrictSchoolPermission',
    }),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeSources',
      'activeStates',
      'activeGrades',
      'activeRaces',
      'activeEthnicSubGroups',
      'activeEthnicities',
      'pidTransactionStatuses',
      'activePersonTypes',
      'activeResidencyStatuses',
      'activeHighestDegrees',
      'activeCertificateTypes',
      'activeItinerantFlags',
      'activeJobAssignments',
      'activeTitles',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),

    batchId() {
      if (this.nearMatches[0].pidBatchId) {
        return this.nearMatches[0].pidBatchId;
      }
      return null;
    },
    exclamationIcon() {
      const icon = 'exclamation-circle';
      return ['fas', icon];
    },
    noMatchStatusIcon() {
      const icon = 'stop-circle';
      return ['fas', icon];
    },
    resolvedStatusIcon() {
      const icon = 'check-circle';
      return ['fas', icon];
    },
    cancelledStatusIcon() {
      const icon = 'window-close';
      return ['fas', icon];
    },
    viewSsnPermission() {
      return 'PRSID:PRPRF:SSN:VIEW';
    },
    cancelSubmissionMessage() {
      return 'Are you sure you want to cancel this submission record?';
    },
    noMatchMessage() {
      return `Are you sure you want to select ${this.properties['BUTTON_LABEL-CREATE_NEW']} and create a new identifier?`;
    },
    matchMessage() {
      return `Are you sure you want to make a "${this.properties['BUTTON_LABEL-ASSIGN_EXISTING']}" decision for the submission record?`;
    },
    topLocation() {
      return 'top';
    },
    bottomLocation() {
      return 'bottom';
    },
    matchStatuses() {
      return [8, 12, 43, 46, 47, 57];
    },
    enabledCdfFieldNames() {
      const goodOnes = this.cdfFieldNames.filter(cdfFieldName => this.getFieldEnabledForPersonType(cdfFieldName, 'ASSIGN', this.nearMatches[0].pidBatch.personTypeCode));
      return goodOnes;
    },
    credentialInfoSectionEnabled() {
      const activeFields = this.credentialFieldNames.filter(field => this.getFieldEnabledForPersonType(field, 'ASSIGN', this.nearMatches[0].pidBatch.personTypeCode));
      return activeFields.length > 0;
    },
    cdfFieldNames() {
      return ['cdf_1',
        'cdf_2',
        'cdf_3',
        'cdf_4',
        'cdf_5',
        'cdf_6',
        'cdf_7',
        'cdf_8',
        'cdf_9',
        'cdf_10',
        'cdf_11',
      ];
    },
    matchingFieldNames() {
      return ['firstName',
        'middleName',
        'lastName',
        'suffix',
        'akaName',
        'fullName',
        'genderCode',
        'dateOfBirth',
        'socialSecurityNumber',
      ];
    },
    personFieldNames() {
      return ['ethnicityIndicator',
        'raceEthnicCode',
        'race2Code',
        'race3Code',
        'race4Code',
        'race5Code',
        'ethnicSubGroup',
        'akaName',
        'fullName',
      ];
    },
    contactFieldNames() {
      return ['street1',
        'street2',
        'city',
        'stateCode',
        'zip',
        'geocode',
      ];
    },
    credentialFieldNames() {
      return ['highestDegree',
        'subjectArea',
        'degreeProvider',
        'certificateNumber',
        'certificateType',
      ];
    },
    locationFieldNames() {
      return ['personType',
        'schoolYear',
        'districtCode',
        'schoolCode',
        'resDistrictCode',
        'sourceCode',
        'altStudentId',
        'altSourceCisCode',
        'locationComment',
        'gradeCode',
      ];
    },
    otherInfoFieldNames() {
      return ['birthLocation',
        'residencyStatus',
        'residencyExpirationDate',
        'mothersMaidenName',
      ];
    },
    transactionFieldNames() {
      return ['submissionTypeCode',
        'transmissionId',
        'pidBatchId',
        'lastUpdateDate',
        'lastUpdateBy',
        'createdDate',
        'createdBy',
      ];
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadSources',
      'loadGrades',
      'loadStates',
      'loadRaces',
      'loadEthnicSubGroups',
      'loadEthnicities',
      'loadPersonTypes',
      'loadPidTransactionStatuses',
      'loadResidencyStatuses',
      'loadHighestDegrees',
      'loadCertificateTypes',
      'loadItinerantFlags',
      'loadJobAssignments',
      'loadTitles',
    ]),
    mapPersonType(personTypeCode) {
      const item = this.activePersonTypes.find(pt => pt.code === personTypeCode);
      return item ? item.name : '\u00A0';
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        const res = await Promise.all([
          this.loadGenders(),
          this.loadSources(),
          this.loadRaces(),
          this.loadGrades(),
          this.loadStates(),
          this.getMatches(),
          this.getMatchIds(),
          this.loadEthnicSubGroups(),
          this.loadEthnicities(),
          this.loadPersonTypes(),
          this.loadPidTransactionStatuses(),
          this.loadResidencyStatuses(),
          this.loadHighestDegrees(),
          this.loadCertificateTypes(),
          this.loadItinerantFlags(),
          this.loadJobAssignments(),
          this.loadTitles(),
        ]);
        this.nearMatches = res[5].data.data;
        this.assignMatchIds(res[6].data);
        this.interpretTransStatus(this.nearMatches[0]);
        this.setFinalMatchId(this.nearMatches[0]);
        await this.getDataFromMaster(this.nearMatches[0]);
        this.getPendingNearMatchCount(this.nearMatches[0]);
        this.newlyCreatedId = this.nearMatches[0].newPidIdCreated;
        this.setPotentialMatchesForDisplay(this.nearMatches[0]);
      } catch (error) {
        this.apiMsg = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.loadingError = true;
        this.hasError = true;
        this.showMessage();
      }
      this.loading = false;
    },
    assignMatchIds(matchIds) {
      this.matchIds = new Map();
      matchIds.forEach(record => this.matchIds.set(record.stateStudentId, record));
    },
    getObjectKeys(group) {
      return this.getFieldLabelsByGroup(group).map(l => l.code
        .toLowerCase());
    },
    formatSsn(ssn) {
      if (ssn) {
        if (ssn.includes('*')) {
          return ssn;
        }
        const numbers = ssn.replace(/[^0-9]/g, '');
        if (numbers.length === 9) {
          return `${numbers.substr(0, 3)}-${numbers.substr(3, 2)}-${numbers.substr(5, 4)}`;
        }
      }
      return '\u00A0';
    },
    formatMMDDYYYY(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return 'N/A';
    },
    compareDate(str1, str2) {
      const date1 = moment(str1).format('MM/DD/YYYY');
      const date2 = moment(str2).format('MM/DD/YYYY');
      if (date1 !== date2) {
        return true;
      }
      return false;
    },
    fieldsNotMatch(submission, nearMatch) {
      if (submission == null || nearMatch == null) {
        return false;
      }
      return areValuesDifferentIgnoringCase(submission, nearMatch);
    },
    fieldsNotMatchSubmission(fieldNames, nearMatchId) {
      const submission = this.nearMatches[0].pidSubmission;

      const items = fieldNames.filter((fieldName) => {
        const subFieldValue = submission[fieldName];
        const masterFieldValue = this.getMasterDataElement(fieldName, nearMatchId);
        return this.fieldsNotMatch(subFieldValue, masterFieldValue);
      });
      return (items && items.length > 0);
    },
    cdfFieldsNotMatchSubmission(nearMatchId) {
      const submission = this.nearMatches[0].pidSubmission;
      const submissionCdf = submission.studentCdf;
      const aPerson = this.getPerson(nearMatchId);
      const masterCdf = aPerson.studentCdf;

      const items = this.enabledCdfFieldNames.filter((fieldName) => {
        const subFieldValue = submissionCdf[fieldName];
        const masterFieldValue = masterCdf[fieldName];
        return this.fieldsNotMatch(subFieldValue, masterFieldValue);
      });
      return (items && items.length > 0);
    },
    locationFieldsNotMatchSubmission(nearMatchId) {
      const submission = this.nearMatches[0].pidSubmission;

      // First, compare fields with same names
      if (this.fieldsNotMatchSubmission(this.locationFieldNames, nearMatchId)) {
        return true;
      }

      // Compare only the date portion of the refresh dates, so a date-only
      // submission and a datetime master value for the same day are not flagged.
      const subRefresh = this.toDateInputValue(submission.refreshDate);
      const masterRefresh = this.getMasterDataElement('sourceRefreshDate', nearMatchId);

      // compare fields that are not named the same
      return (this.fieldsNotMatch(submission.localStudentId, this.getMasterDataElement('localId', nearMatchId))
        || this.fieldsNotMatch(submission.locationCount, this.getMasterDataElement('activeLocationCount', nearMatchId))
        || this.fieldsNotMatch(subRefresh, this.toDateInputValue(masterRefresh)));
    },
    convertToString(value) {
      return value.toString();
    },
    getMatches() {
      return axios.get(`/personid/transactions/details?pidSubmissionId=${this.nearMatchId}&getDetails=true`);
    },
    getMatchIds() {
      return axios.get(`/personid/transactions/${this.nearMatchId}/matchids`);
    },
    matchPermission(permission) {
      return {
        permission,
        districtCode: this.nearMatches[0].districtCode,
        schoolCode: this.nearMatches[0].schoolCode,
      };
    },
    interpretTransStatus(aNearMatch) {
      if (aNearMatch.transStatus === 6) {
        this.decisionStatus = 'Pending';
        this.summaryStatus.statusName = 'Pending';
        this.summaryStatus.statusIcon = this.exclamationIcon;
      } else if (aNearMatch.transStatus === 7 || aNearMatch.transStatus === 44) {
        this.isResolvedNoMatch = true;
        this.decisionStatus = 'No Match';
        this.summaryStatus.statusName = 'Resolved';
        this.summaryStatus.statusIcon = this.noMatchStatusIcon;
      } else if (this.matchStatuses.includes(aNearMatch.transStatus)) {
        this.isResolvedMatch = true;
        this.decisionStatus = 'Match';
        this.summaryStatus.statusName = 'Resolved';
        this.summaryStatus.statusIcon = this.resolvedStatusIcon;
      } else if (aNearMatch.transStatus === 9 || aNearMatch.transStatus === 19) {
        this.decisionStatus = 'Canceled';
        this.summaryStatus.statusName = 'Canceled';
        this.summaryStatus.statusIcon = this.cancelledStatusIcon;
      } else {
        this.decisionStatus = 'UNKNOWN';
        this.summaryStatus.statusName = 'UNKNOWN';
      }
    },
    getPotentialMatchSubmissionIds(aNearMatch) {
      return aNearMatch.pidMatchTransDetail.pidSubmissionId;
    },
    async getDataFromMaster(aNearMatch) {
      try {
        const pidSubmissionId = this.getPotentialMatchSubmissionIds(aNearMatch);
        const params = new URLSearchParams();
        params.append('submissionId', pidSubmissionId);
        const res = await axios.get('person/potentialmatches', { params });
        this.masterData = new Map();
        res.data.forEach(student => this.masterData.set(student.stateStudentId, student));
      } catch (error) {
        if (error.response.status === 500) {
          this.apiMsg = 'Network Error';
        } else {
          this.apiMsg = `${error.response.data.message}`;
        }
        this.showError(this.apiMsg);
      } finally {
        this.showModal = false;
      }
    },
    getPerson(stateStudentId) {
      const aPerson = this.masterData.get(stateStudentId);
      if (!aPerson) {
        this.apiMsg = `Matched State Student Id not found in Master: ${stateStudentId}`;
        this.showError(this.apiMsg);
      }
      return aPerson;
    },
    getMasterDataElement(elementName, stateStudentId) {
      const aPerson = this.getPerson(stateStudentId);
      if (aPerson) {
        const aProperty = Object.getOwnPropertyDescriptor(aPerson, elementName);
        return (aProperty) ? aProperty.value : '';
      }
      return '';
    },
    getMatchPercent(stateStudentId) {
      const matchIdObject = this.matchIds.get(stateStudentId);
      const matchFraction = (matchIdObject ? matchIdObject.matchPercent : null);
      if (matchFraction) {
        return matchFraction * 100;
      }
      return null;
    },
    getMatchNote(stateStudentId) {
      const matchIdObject = this.matchIds.get(stateStudentId);
      return (matchIdObject ? matchIdObject.note : null);
    },
    getMasterCdfDataElement(elementName, stateStudentId) {
      const aPerson = this.getPerson(stateStudentId);
      if (aPerson) {
        const studentCdfProperty = Object.getOwnPropertyDescriptor(aPerson, 'studentCdf');
        if (!studentCdfProperty) {
          return '';
        }
        const cdfProperty = Object.getOwnPropertyDescriptor(studentCdfProperty.value, elementName);
        return (cdfProperty) ? cdfProperty.value : '';
      }
      return '';
    },
    setFinalMatchId(aNearMatch) {
      if (aNearMatch.pidMatchTransId.match) {
        this.finalMatchId = aNearMatch.pidMatchTransId.stateStudentId;
      }
    },
    showMessage() {
      const type = this.hasError ? 'danger' : 'success';
      this.showNotification(type, this.messages);
    },
    setUpdateMaster(result) {
      if (result.doUpdateOut) {
        this.doNotUpdateMasterIndicators.delete(result.potentialMatchIdOut);
      } else {
        this.doNotUpdateMasterIndicators.add(result.potentialMatchIdOut);
      }
    },
    toggleModalMatch(mode, aPotentialMatchid, linkName, index) {
      this.finalMatchId = aPotentialMatchid;
      this.toggleModal(mode, linkName, index);
    },
    toggleModal(mode, linkName, index) {
      if (mode === 'Cancel Submission Record') {
        this.userAction.actionConfirmMessage = this.cancelSubmissionMessage;
        this.userAction.actionMethod = 'cancelSubmission';
        this.userAction.actionConfirmHeader = mode;
        this.userAction.actionConfirmButtonText = mode;
      } else if (mode === 'No Match') {
        this.userAction.actionConfirmMessage = this.noMatchMessage;
        this.userAction.actionMethod = 'performNoMatch';
        this.userAction.actionConfirmHeader = 'Confirm Decision';
        this.userAction.actionConfirmButtonText = 'Yes Create a New ID';
      } else if (mode === 'Match') {
        this.userAction.actionConfirmMessage = this.matchMessage;
        this.userAction.actionMethod = 'performMatch';
        this.userAction.actionConfirmHeader = 'Confirm Decision';
        this.userAction.actionConfirmButtonText = `Yes Select ${this.properties['BUTTON_LABEL-ASSIGN_EXISTING']}`;
      }
      this.showModal = !this.showModal;
      if (this.showModal) {
        if (linkName) {
          if (index >= 0) {
            this.refEle = this.$refs[linkName][index];
          } else {
            this.refEle = this.$refs[linkName];
          }
        }
        this.$nextTick(() => {
          if (this.$refs.closeIcon) {
            this.$refs.closeIcon.focus();
          }
          this.modalTrapFocus();
        });
        this.$el.addEventListener('keydown', this.modalHandleEscKey);
      } else if (!this.showModal) {
        this.$nextTick(() => {
          if (this.refEle) {
            this.refEle.focus();
          }
        });
        this.$el.removeEventListener('keydown', this.modalHandleEscKey);
      }
    },
    processUserAction() {
      this[this.userAction.actionMethod]();
    },
    showError(error) {
      this.showNotification('danger', Array.isArray(error) ? error : [error]);
    },
    showSuccess(message) {
      this.showNotification('success', Array.isArray(message) ? message : [message]);
    },
    async cancelSubmission() {
      try {
        this.loading = true;
        await axios.put(`personid/transactions/${this.nearMatchId}`,
          { command: 'CANCEL', originalPidSubmissionId: this.nearMatches[0].pidSubmission.id });
        this.showSuccess('The submission has been canceled successfully.');
      } catch (error) {
        if (error.response.status === 500) {
          this.apiMsg = 'Network Error';
        } else {
          this.apiMsg = `${error.response.data.message}`;
        }
        this.showError(this.apiMsg);
      } finally {
        this.loading = false;
        this.showModal = false;
        await this.onLoad();
      }
    },
    async getPendingNearMatchCount(myNearMatch) {
      const params = new URLSearchParams();
      params.append('pidBatchId', myNearMatch.pidBatchId);
      const res = await axios.get('personid/transactions/totalopen', { params });
      this.pendingCount = res.data;
    },
    async performNoMatch() {
      try {
        this.loading = true;
        await axios.put(`personid/transactions/${this.nearMatchId}`,
          { command: 'CREATE_NEW', originalPidSubmissionId: this.nearMatches[0].pidSubmission.id });
        this.showSuccess('No match decision - new ID created.');
      } catch (error) {
        if (error.response.status === 500) {
          this.apiMsg = 'Network Error';
        } else {
          this.apiMsg = `${error.response.data.message}`;
        }
        this.showError(this.apiMsg);
      } finally {
        this.loading = false;
        this.showModal = false;
        await this.onLoad();
      }
    },
    async performMatch() {
      try {
        this.loading = true;
        const updateMasterParam = !this.doNotUpdateMasterIndicators.has(this.finalMatchId);
        await axios.put(`personid/transactions/${this.nearMatchId}`,
          {
            command: 'ASSIGN_ID',
            personIdForAssign: this.finalMatchId,
            originalPidSubmissionId: this.nearMatches[0].pidSubmission.id,
            okToUpdatePersonForASSIGN_ID: updateMasterParam,
          });
        this.showSuccess('Match completed successfully.');
      } catch (error) {
        if (error.response.status === 500) {
          this.apiMsg = 'Network Error';
        } else {
          this.apiMsg = `${error.response.data.message}`;
        }
        this.showError(this.apiMsg);
      } finally {
        this.loading = false;
        this.showModal = false;
        await this.onLoad();
      }
    },
    hasNearMatchSsnSameResolution() {
      return this.nearMatches[0].pidMatchTransDetail.response.resolutionReason === 'NEAR_MATCH_NO_DEMOGRAPHIC_MATCHES_BUT_MATCHED_SSNS';
    },
    userCanPerformMatch() {
      const hasMatchPermission = this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:MATCHDEC:ASSNEXIST'));
      const hasSsnSamePermission = this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:ASSIGN:SSNSAME'));
      return this.hasNearMatchSsnSameResolution()
        ? hasSsnSamePermission
        : hasMatchPermission;
    },
    hasIdRelatedNoMatchPermission() {
      if (this.nearMatches[0].pidSubmission.stateStudentId) {
        return this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:CREATEID:IDPRESENT'));
      }
      return this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:CREATEID:IDNOTPRESENT'));
    },
    verifyNoMatchSsnsame() {
      return this.hasNearMatchSsnSameResolution()
        ? this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:CREATEID:SSNSAME'))
        : true;
    },
    userCanPerformNoMatch() {
      const hasSsnSamePermissionForCreate = this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:CREATEID:SSNSAME'));
      return this.hasNearMatchSsnSameResolution()
        ? hasSsnSamePermissionForCreate
        : this.hasIdRelatedNoMatchPermission();
    },
    userHasUpdateMasterPermission(aPotentialMatchid) {
      const isReliable = this.checkReliabilityScore(aPotentialMatchid);
      const hasPermission = this.hasDistrictSchoolPermission(this.matchPermission('PRSID:MATCH:RESOLVENM:UPDATEMASTER'));
      return isReliable && hasPermission;
    },
    checkReliabilityScore(aPotentialMatchid) {
      const thisStudent = this.masterData.get(aPotentialMatchid);
      const potentialMatchScore = (thisStudent ? thisStudent.sourceReliability : null);
      return potentialMatchScore
        && this.nearMatches[0].pidSubmissionReliabilityScore >= potentialMatchScore;
    },
    anyFieldsEnabled(fieldNames) {
      // check whether any field is enabled
      const items = fieldNames.filter(fn => this.getFieldEnabledForPersonType(fn, 'ASSIGN', this.nearMatches[0].pidSubmission));
      return (items && items.length > 0);
    },
    setPotentialMatchesForDisplay(aNearMatch) {
      // If the status <> 'Match', return potential matches as is
      if (this.decisionStatus !== 'Match') {
        this.potentialMatchesForDisplay = aNearMatch.pidMatchTransDetail.response.potentialMatches;
        return;
      }
      // Return potential matches with the matched record first
      const matchedId = aNearMatch.pidMatchTransId.stateStudentId;
      const matchedPotentialObject = aNearMatch.pidMatchTransDetail.response.potentialMatches
        .find(potentialMatch => potentialMatch.id === matchedId);
      this.potentialMatchesForDisplay = aNearMatch.pidMatchTransDetail.response.potentialMatches
        .filter(potentialMatch => potentialMatch.id !== matchedId);
      this.potentialMatchesForDisplay.unshift(matchedPotentialObject);
    },
    getRecordStatusDescription(statusId) {
      let statusDesc = 'null';
      this.pidTransactionStatuses.forEach((r) => {
        r.list.forEach((i) => {
          if (i.id === statusId) {
            statusDesc = i.description;
          }
        });
      });
      return statusDesc;
    },
    toggleDetails(section) {
      this.expandedSections[section] = !this.expandedSections[section];
    },
  },
  filters: {

    getDescripton(value, list) {
      if (value) {
        if (value === '*') {
          return value;
        }
        const item = list.find(g => g.code === value);
        if (item) {
          return item.description;
        }
      }
      return '\u00A0';
    },
    OrPlaceHolder(value) {
      if (value) {
        return value;
      }
      return '\u00A0';
    },
    OrZeroPlaceHolder(value) {
      if (value) {
        return value;
      }
      return '\u0030';
    },
  },

};
</script>

<style lang="sass" scoped>
  .panel--highlighted
    background-color: rgb(255, 245, 214)
  .padding--1rem
    padding: 1rem
  .margin-bottom--half
    margin-bottom: 0.5rem
  .submission-header
    font-size: 12px
    color: #737373
  .compare-panel
    padding: 1rem
    min-width: 320px
    max-width: 320px
    // flex-wrap: wrap
  .compare-panel--cell
    padding: 0.5rem
    // border: 1px solid grey
  .compare-panel--heading
    background-color: #ccc !important
    padding: 0.5rem
    position: relative
  .compare--content
    font-size: 16px
    font-weight: bold
    text-align: left
    word-wrap: break-word
  .compare-panel-toggle
    position: absolute
    right: 0.5rem
    top: 50%
    transform: translateY(-50%)
    &.button-link
      background-color: transparent
      border: none
      font-size: 0.85rem
  .source-label
    margin-top: 6px
    background-color: #ccc
    display: inline-block
    padding: 5px 5px 5px 5px

  .percent-label
    font-style: bold
    color: black
    font-size: 1.1rem
    font-weight: 800
    padding: .25rem 0 .25rem .15rem

  table
    border-collapse: separate
    tbody
      th:first-child,
      td:first-child
        position: sticky
        left: 0
        background-color: #fff
        z-index: 1
      tr:last-child td
          border-bottom-width: 1px
      th:not(:last-child),
      td:not(:last-child)
        border-right: 1px solid #ccc
  th
    font-weight: 400
    vertical-align: middle
    color: black

  td
    min-width: 320px
    max-width: 320px

  .outer
    // position: relative
    // margin-left: auto
    padding: 1rem 0
    display: flex
    justify-content: center
  .inner
    overflow-x: auto
    overflow-y: visible
    // margin-left: 320px
    max-width: max-content
  .modal-card-title
    display: flex
  .modal-body
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none

  .text-align--center
    text-align: center
  .vertical-align--initial
    vertical-align: initial
  .vertical-align--bottom
    vertical-align: bottom
  .separator
    border-left: 1px solid #ccc
    height: 42px
  .list-content
    height: 100%
    display: flex
    flex-direction: column
    justify-content: center
    &.p-narrow
      padding-left: 1rem
      padding-right: 1rem
      width: auto
    .list-header
      .list-title
        font-size: 1.15rem
        color: #000
        font-weight: 700
      .list-subtitle
        font-size: .85rem
        color: #666
  .modal-card-head
    background-color: #F5F5F5
    text-align: left
  .modal-card-title
    margin-bottom: 0 !important
  .modal-card-body
    border-bottom-right-radius: .4rem
    border-bottom-left-radius: .4rem
    text-align: left
  .modal-body
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start
  .update-box
    display: flex
    align-items: center
    justify-content: flex-end
    button
      margin-left: 1rem
  .summary
    display: flex
    align-items: center
    justify-content: flex-end
    flex-wrap: wrap
    height: 100%
</style>
